import { FilterIcon, XIcon } from "lucide-react"
import type * as React from "react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { CustomerPicker } from "@/features/invoices/DocumentPickers"
import { useCustomer } from "@/features/customers/use-customer"
import { useInvoice } from "@/features/invoices/use-invoice"
import type { useCreditNoteFilters } from "@/features/credit-notes/use-credit-note-list"
import { Badge } from "@/components/ui/badge"
import { DateField } from "@/components/DateField"
import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Label } from "@/components/ui/label"
import { RupeeBound } from "@/components/RupeeBound"
import { useFormatDay } from "@/lib/dates"
import { DATE_PRESETS, activeDatePreset } from "@/lib/date-presets"
import { formatRupees } from "@/lib/money"

/**
 * The credit-note register's filters, behind one button (§17.3, §11.1).
 *
 * ═════════════════════════════════════════════════════════════════════════════
 * THE SAME SHEET AS THE OTHER THREE REGISTERS, PLUS THE ONE QUESTION ONLY A
 * CREDIT NOTE RAISES: which BILL is this against?
 *
 * `CreditNoteListQuerySchema` is `.strict()` and declares `q`, `sort`, `dir`,
 * `status`, `invoiceId`, `customerId`, `from`, `to`, `minTotalPaise`,
 * `maxTotalPaise`, `limit` and `offset` — nothing else. So there is no
 * `issuedManually` here (that flag is §6.1 and lives on `model Invoice` alone),
 * no `openOnly` and no `overdueOnly` (§17.7's questions about goods still out),
 * and no type select: a credit note has one kind. A control for any of them
 * would earn a 400 rather than being politely ignored.
 *
 * THE `invoiceId` FILTER IS NOT SET FROM THIS SHEET, AND THAT IS DELIBERATE.
 * It arrives by LINK — `routes.creditNotesForInvoice(id)` is what the credited
 * figure on a bill opens — so what this file owes it is a CHIP saying it is on,
 * naming the bill, and a way to take it off. Offering a "pick an invoice"
 * control here would be a second way to answer a question the bill's own screen
 * already answers better, and it would need an invoice picker that exists
 * nowhere else in the app.
 *
 * THE AMOUNT RANGE BOUNDS `grand_total_paise` — the amount CREDITED, which is
 * exactly what the row prints. Unlike the quotation register there is no second
 * candidate: `tax_display_mode` exists on `model Quotation` alone, so the stored
 * total and the printed total are one number. Both bounds are non-negative, and
 * here that is structural rather than conventional — there is no negative amount
 * anywhere in this table (§17.3), so a negative bound would filter for rows that
 * cannot exist.
 *
 * "SEARCH BY NAME" IS A CUSTOMER PICKER, NOT A TEXT BOX. `q` on this endpoint
 * matches `credit_note_number` and the frozen `original_invoice_number` and
 * NOTHING else — the buyer lives in `customer_snapshot`, a text-backed JSON
 * column the server will not query inside (CLAUDE.md).
 * ═════════════════════════════════════════════════════════════════════════════
 */
export function CreditNoteFilterSheet({
  filters,
  setFilters,
  clearFilters,
  activeCount,
}: {
  filters: ReturnType<typeof useCreditNoteFilters>["filters"]
  setFilters: ReturnType<typeof useCreditNoteFilters>["setFilters"]
  clearFilters: () => void
  activeCount: number
}): React.JSX.Element {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [pickingCustomer, setPickingCustomer] = useState(false)

  /*
   * The URL holds only the id, so the NAME has to be fetched to show it. Without
   * this the button would read "Customer #4", which is the database's way of
   * identifying Lupin, not the owner's.
   */
  const chosen = useCustomer(
    filters.customerId === undefined ? undefined : String(filters.customerId),
  )

  const activePreset = activeDatePreset(filters.from, filters.to)

  return (
    <>
      <Button
        type="button"
        variant="outline"
        onClick={() => {
          setOpen(true)
        }}
      >
        <FilterIcon />
        {/* The count is the whole point of collapsing them: a filtered register
          * that LOOKS unfiltered is how someone concludes their credit notes are
          * gone. */}
        {activeCount === 0
          ? t("list.moreFilters")
          : t("list.moreFiltersCount", { n: activeCount })}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="flex max-h-[85vh] flex-col gap-4 overflow-y-auto sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{t("list.moreFilters")}</DialogTitle>
            <DialogDescription>{t("creditNote.listSubtitle")}</DialogDescription>
          </DialogHeader>

          {/* ── Customer ─────────────────────────────────────────────────── */}
          <div className="flex flex-col gap-1.5">
            <Label>{t("lbl.customer")}</Label>
            {filters.customerId === undefined ? (
              <Button
                type="button"
                variant="outline"
                className="justify-start"
                onClick={() => {
                  setPickingCustomer(true)
                }}
              >
                {t("list.filterByCustomer")}
              </Button>
            ) : (
              <div className="flex items-center justify-between gap-2 rounded-md border p-2">
                <span className="min-w-0 truncate text-sm">
                  {chosen.data?.name ?? t("msg.loading")}
                </span>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setFilters({ customerId: undefined })
                  }}
                >
                  <XIcon className="size-4" />
                  <span className="sr-only">{t("list.clearCustomerFilter")}</span>
                </Button>
              </div>
            )}
          </div>

          {/* ── Credit note date ─────────────────────────────────────────── */}
          {/* The date the NOTE was issued (`credit_note_date`), not the date of
            * the bill it reduces — those are different months routinely, and it
            * is this one that decides which GSTR-1 the note is declared in.
            * Presets come from `lib/date-presets`, shared with the other three
            * registers so "This year" cannot come to mean two things. */}
          <div className="flex flex-col gap-2">
            <Label>{t("list.creditNoteDate")}</Label>

            <div className="flex flex-wrap gap-2">
              {DATE_PRESETS.map((preset) => (
                <Button
                  key={preset.id}
                  type="button"
                  variant={activePreset?.id === preset.id ? "default" : "outline"}
                  onClick={() => {
                    setFilters(preset.range())
                  }}
                >
                  {t(preset.labelKey)}
                </Button>
              ))}
            </div>

            <div className="grid grid-cols-2 gap-2">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="credit-note-filter-from" className="text-muted-foreground text-xs">
                  {t("list.dateFrom")}
                </Label>
                {/* The app's own calendar, never `<input type="date">` — that
                  * renders in the BROWSER's locale, so a Marathi screen opened
                  * an English picker (§11.4). */}
                <DateField
                  id="credit-note-filter-from"
                  clearable
                  value={filters.from ?? ""}
                  placeholder={t("list.dateFrom")}
                  onChange={(value) => {
                    setFilters({ from: value === "" ? undefined : value })
                  }}
                />
              </div>
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="credit-note-filter-to" className="text-muted-foreground text-xs">
                  {t("list.dateTo")}
                </Label>
                <DateField
                  id="credit-note-filter-to"
                  clearable
                  value={filters.to ?? ""}
                  placeholder={t("list.dateTo")}
                  onChange={(value) => {
                    setFilters({ to: value === "" ? undefined : value })
                  }}
                />
              </div>
            </div>
          </div>

          {/* ── Amount credited ──────────────────────────────────────────── */}
          {/* The control is `components/RupeeBound`, shared with the other three
            * sheets: the owner types rupees, the URL and the API carry integer
            * paise, and an empty box is "no filter" rather than zero. */}
          <div className="flex flex-col gap-1.5">
            <Label>{t("creditNote.amountRange")}</Label>
            <div className="grid grid-cols-2 gap-2">
              <RupeeBound
                id="credit-note-filter-min-amount"
                label={t("list.amountFrom")}
                paise={filters.minTotalPaise}
                onChange={(minTotalPaise) => {
                  setFilters({ minTotalPaise })
                }}
              />
              <RupeeBound
                id="credit-note-filter-max-amount"
                label={t("list.amountTo")}
                paise={filters.maxTotalPaise}
                onChange={(maxTotalPaise) => {
                  setFilters({ maxTotalPaise })
                }}
              />
            </div>
          </div>

          <DialogFooter className="gap-2 sm:justify-between">
            <Button
              type="button"
              variant="ghost"
              disabled={activeCount === 0}
              onClick={() => {
                clearFilters()
              }}
            >
              {t("list.clearFilters")}
            </Button>
            <Button
              type="button"
              onClick={() => {
                setOpen(false)
              }}
            >
              {t("btn.done")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <CustomerPicker
        open={pickingCustomer}
        onOpenChange={setPickingCustomer}
        onPick={(customer) => {
          setFilters({ customerId: customer.id })
        }}
      />
    </>
  )
}

/**
 * The filters currently on, as removable chips under the toolbar.
 *
 * The status is NOT a chip: it has its own visible select in the toolbar, and
 * repeating it here would let the owner clear it from one place while the other
 * still shows it set.
 *
 * THE `invoiceId` CHIP IS THE IMPORTANT ONE. That filter arrives by LINK from a
 * bill's credited figure, so the register can open already narrowed to one
 * invoice with nothing else on screen to say why only two rows are showing —
 * which is exactly how a register stops being trusted. It names the bill by its
 * number, fetched for the purpose, because `invoiceId=7` is the database's way
 * of naming a document and `26-27/67` is the owner's.
 */
export function ActiveCreditNoteFilterChips({
  filters,
  setFilters,
}: {
  filters: ReturnType<typeof useCreditNoteFilters>["filters"]
  setFilters: ReturnType<typeof useCreditNoteFilters>["setFilters"]
}): React.JSX.Element | null {
  const { t } = useTranslation()
  const formatDay = useFormatDay()
  const chosen = useCustomer(
    filters.customerId === undefined ? undefined : String(filters.customerId),
  )
  const invoice = useInvoice(
    filters.invoiceId === undefined ? undefined : String(filters.invoiceId),
  )

  const chips: { id: string; label: string; clear: () => void }[] = []

  if (filters.invoiceId !== undefined) {
    chips.push({
      id: "invoice",
      /*
       * The bill's NUMBER while it is known, falling back to "loading" rather
       * than to the id: printing `#7` would be the primary key leaking onto the
       * screen, and the owner has never seen it.
       */
      label: t("creditNote.againstRow", {
        number: invoice.data?.number ?? t("msg.loading"),
      }),
      clear: () => {
        setFilters({ invoiceId: undefined })
      },
    })
  }
  if (filters.customerId !== undefined) {
    chips.push({
      id: "customer",
      label: chosen.data?.name ?? t("msg.loading"),
      clear: () => {
        setFilters({ customerId: undefined })
      },
    })
  }
  if (filters.from !== undefined || filters.to !== undefined) {
    chips.push({
      id: "dates",
      /* Through `formatDay`, so the chip reads "1 Aug 2026" like every other
       * date in the app. A raw `2026-08-01` here is the wire format leaking onto
       * the screen. */
      label: `${filters.from === undefined ? "…" : formatDay(filters.from)} → ${
        filters.to === undefined ? "…" : formatDay(filters.to)
      }`,
      clear: () => {
        setFilters({ from: undefined, to: undefined })
      },
    })
  }
  if (filters.minTotalPaise !== undefined || filters.maxTotalPaise !== undefined) {
    chips.push({
      id: "amount",
      /* Through `formatRupees`, so the chip reads "₹1,45,600.00" with Indian
       * digit grouping like every other amount in the app — never
       * `toLocaleString`, and never the raw paise integer. */
      label: `${
        filters.minTotalPaise === undefined ? "…" : formatRupees(filters.minTotalPaise)
      } → ${filters.maxTotalPaise === undefined ? "…" : formatRupees(filters.maxTotalPaise)}`,
      /* BOTH ENDS COME OFF TOGETHER. The chip shows one range, so clearing it
       * must leave no half-range behind narrowing the register invisibly. */
      clear: () => {
        setFilters({ minTotalPaise: undefined, maxTotalPaise: undefined })
      },
    })
  }

  if (chips.length === 0) return null

  return (
    <div className="flex flex-wrap gap-2">
      {chips.map((chip) => (
        /*
         * Each chip is REMOVABLE where it is shown. Sending someone back into
         * the dialog to undo one filter is the reason people give up and clear
         * everything, losing the two they wanted.
         */
        <Badge key={chip.id} variant="secondary" className="gap-1 py-1 pr-1 pl-2">
          <span className="max-w-48 truncate">{chip.label}</span>
          <button
            type="button"
            onClick={chip.clear}
            aria-label={t("list.clearFilters")}
            className="hover:bg-background/60 rounded-sm p-0.5"
          >
            <XIcon className="size-3" />
          </button>
        </Badge>
      ))}
    </div>
  )
}
